import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../theme/ThemeContext';
import Button from './button';
import downloadImageWhite from '../../static/images/download_icon_white.png';
import downloadImageBlack from '../../static/images/download_icon_black.png';


export default function EnrichmentScatterPlot({ foldChangeData }) {
    const containerRef = useRef();
    const svgRef = useRef();
    const { theme } = useTheme();

    const downloadImage = theme.name === 'light' ? downloadImageBlack : downloadImageWhite;

    const downloadCSV = () => {
        if (!foldChangeData || foldChangeData.length === 0) return;

        const sortedData = [...foldChangeData].sort((a, b) => b.sampleCount - a.sampleCount);

        const header = 'taxon,count,log2fc\n';
        const csvContent = sortedData.reduce((acc, row) => {
            // escape taxon name if it contains commas
            const taxonName = row.name.includes(',') ? `"${row.name}"` : row.name;
            return acc + `${taxonName},${row.sampleCount},${row.log2fc.toFixed(4)}\n`;
        }, header);

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'enrichment_data.csv';

        document.body.appendChild(a);
        a.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const drawChart = () => {
        if (!foldChangeData || !containerRef.current) return;

        d3.select(svgRef.current).selectAll('*').remove();

        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerWidth * 0.6;
        const margin = { top: 40, right: 40, bottom: 60, left: 60 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current)
            .attr('width', containerWidth)
            .attr('height', containerHeight)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // scales
        const xScale = d3.scaleLinear()
            .domain([0, d3.max(foldChangeData, d => d.sampleCount)])
            .range([0, width])
            .nice();

        const yScale = d3.scaleLinear()
            .domain([
                d3.min(foldChangeData, d => d.log2fc),
                d3.max(foldChangeData, d => d.log2fc)
            ])
            .range([height, 0])
            .nice();

        //  axes
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            // .call(d3.axisBottom(xScale))
            .call(
                d3.axisBottom(xScale)
                    .ticks(5)
                    .tickFormat(d3.format('d'))
            )
            .attr('color', theme.colors.text);

        svg.append('g')
            .call(d3.axisLeft(yScale))
            .attr('color', theme.colors.text);

        // zero line
        svg.append('line')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', yScale(0))
            .attr('y2', yScale(0))
            .attr('stroke', theme.colors.text)
            .attr('stroke-dasharray', '4');

        const showCopyFeedback = (circle) => {
            const originalFill = circle.style('fill');
            const originalOpacity = circle.style('opacity');

            circle
                .style('fill', theme.colors.primary)
                .style('opacity', 1)
                .transition()
                .duration(500)
                .style('fill', originalFill)
                .style('opacity', originalOpacity);
        };

        svg.selectAll('circle')
            .data(foldChangeData)
            .enter()
            .append('circle')
            .attr('cx', d => xScale(d.sampleCount))
            .attr('cy', d => yScale(d.log2fc))
            .attr('r', 4)
            .attr('fill', d => d.log2fc > 0 ? 'rgb(46, 139, 87)' : 'rgb(233, 150, 122)')
            .attr('opacity', 0.6)
            .style('cursor', 'pointer')
            .on('click', async (event, d) => {
                try {
                    await navigator.clipboard.writeText(d.name);
                    showCopyFeedback(d3.select(event.target));
                } catch (err) {
                    console.error('Failed to copy to clipboard:', err);
                }
            });

        // labels
        svg.append('text')
            .attr('x', width / 2)
            .attr('y', height + 40)
            .attr('text-anchor', 'middle')
            .attr('fill', theme.colors.text)
            .text('Number of subsamples containing taxon');

        svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -height / 2)
            .attr('y', -40)
            .attr('text-anchor', 'middle')
            .attr('fill', theme.colors.text)
            .text('Log2 fold change');

        const tooltip = d3.select(containerRef.current)
            .append('div')
            .attr('class', 'tooltip')
            .style('opacity', 0)
            .style('position', 'absolute')
            .style('background-color', theme.colors.surface)
            .style('color', theme.colors.text)
            .style('padding', '10px')
            .style('border-radius', '5px')
            .style('pointer-events', 'none')
            .style('z-index', 100);

        let isHovering = false;

        const updateTooltip = (event, d) => {
            const containerRect = containerRef.current.getBoundingClientRect();
            const circleRect = event.target.getBoundingClientRect();

            tooltip
                .style('opacity', 0.9)
                .html(`
                    <strong>Taxon:</strong> ${d.name}<br/>
                    <strong>Present in:</strong> ${d.sampleCount} samples<br/>
                    <strong>Log2FC:</strong> ${d.log2fc.toFixed(2)}
                `);

            const tooltipRect = tooltip.node().getBoundingClientRect();
            let tooltipX = (circleRect.left - containerRect.left) + circleRect.width + 10;
            // let tooltipY = (circleRect.top - containerRect.top) - (tooltipRect.height / 2);
            let tooltipY = (circleRect.top - containerRect.top) + 10;

            const maxLeft = containerRect.width - tooltipRect.width;
            const maxTop = containerRect.height - tooltipRect.height;

            if (tooltipX > maxLeft) tooltipX = maxLeft;
            if (tooltipY < 0) tooltipY = 0;
            if (tooltipY > maxTop) tooltipY = maxTop;

            tooltip
                .style('left', `${tooltipX}px`)
                .style('top', `${tooltipY}px`);
        };

        svg.selectAll('circle')
            .on('mouseover', (event, d) => {
                isHovering = true;
                updateTooltip(event, d);
            })
            .on('mouseout', () => {
                isHovering = false;
                setTimeout(() => {
                    if (!isHovering) {
                        tooltip.transition()
                            // .duration(500)
                            .duration(0)
                            .style('opacity', 0);
                    }
                }, 50);
            });
    };

    useEffect(() => {
        drawChart();
        const handleResize = () => drawChart();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [foldChangeData, theme]);

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
            <svg ref={svgRef} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <Button onClick={downloadCSV}>
                    CSV
                    <img src={downloadImage} alt="download CSV" style={{ marginLeft: '8px', height: '1em' }} />
                </Button>
            </div>
        </div>
    );
}